/**
 * 2D plan renderer.
 *
 * Draws directly from the compiler's boundary runs and occupancy — the same
 * records the 3D geometry came from — so the plan cannot disagree with the
 * level you are standing in.
 */

import type { CompiledLevel } from 'levelspec';
import { MARKER_STYLE } from '../three/adapter.ts';

const WALL_COLOR: Record<string, string> = {
  hard: '#c8d2dd',
  soft: '#e39b45',
  reinforced: '#5b9dff',
  glass: '#7fd4e0',
  open: '#2f7f5a',
};

const OPENING_COLOR: Record<string, string> = {
  door: '#7de08a',
  arch: '#7de08a',
  open: '#7de08a',
  breach: '#ff8a5c',
  window: '#7fd4e0',
  rappel_window: '#a78bfa',
  rappel_door: '#a78bfa',
  hatch_frame: '#e0b25c',
};

export interface PlanOptions {
  layerId: string;
  player?: { x: number; y: number; yaw: number };
  showLabels?: boolean;
  padding?: number;
  background?: string;
}

interface Fit {
  scale: number;
  ox: number;
  oy: number;
  h: number;
}

function fitFor(level: CompiledLevel, layerId: string, w: number, h: number, pad: number): Fit | null {
  const occ = level.occupancy.get(layerId);
  if (!occ || occ.size === 0) return null;
  const g = level.spec.grid ?? 1;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const k of occ.keys()) {
    const i = k.indexOf(',');
    const x = Number(k.slice(0, i));
    const y = Number(k.slice(i + 1));
    minX = Math.min(minX, x * g);
    minY = Math.min(minY, y * g);
    maxX = Math.max(maxX, (x + 1) * g);
    maxY = Math.max(maxY, (y + 1) * g);
  }
  const scale = Math.min((w - pad * 2) / (maxX - minX), (h - pad * 2) / (maxY - minY));
  return {
    scale,
    ox: pad - minX * scale + (w - pad * 2 - (maxX - minX) * scale) / 2,
    oy: pad - minY * scale + (h - pad * 2 - (maxY - minY) * scale) / 2,
    h,
  };
}

export function drawPlan(
  ctx: CanvasRenderingContext2D,
  level: CompiledLevel,
  width: number,
  height: number,
  opts: PlanOptions,
): void {
  const pad = opts.padding ?? 14;
  ctx.clearRect(0, 0, width, height);
  if (opts.background) {
    ctx.fillStyle = opts.background;
    ctx.fillRect(0, 0, width, height);
  }
  const fit = fitFor(level, opts.layerId, width, height, pad);
  if (!fit) return;
  const g = level.spec.grid ?? 1;
  const px = (x: number): number => fit.ox + x * fit.scale;
  const py = (y: number): number => fit.h - (fit.oy + y * fit.scale);

  // Floor footprint, one fill per space.
  const occ = level.occupancy.get(opts.layerId)!;
  const layer = level.spec.layers.find((l) => l.id === opts.layerId);
  const spaceOrder = new Map((layer?.spaces ?? []).map((s, i) => [s.id, i]));
  ctx.globalAlpha = 1;
  for (const [k, space] of occ) {
    const i = k.indexOf(',');
    const x = Number(k.slice(0, i));
    const y = Number(k.slice(i + 1));
    const idx = spaceOrder.get(space) ?? 0;
    const hue = (idx * 47) % 360;
    ctx.fillStyle = `hsl(${hue} 22% 24%)`;
    ctx.fillRect(px(x * g), py((y + 1) * g), g * fit.scale + 0.6, g * fit.scale + 0.6);
  }

  // Cover.
  ctx.fillStyle = 'rgba(190,170,110,0.5)';
  for (const c of level.spec.covers ?? []) {
    if (c.layer !== opts.layerId) continue;
    ctx.fillRect(
      px(c.rect[0] * g),
      py((c.rect[1] + c.rect[3]) * g),
      c.rect[2] * g * fit.scale,
      c.rect[3] * g * fit.scale,
    );
  }

  // Walls, split around their openings — read straight from the boundary runs.
  ctx.lineCap = 'butt';
  for (const b of level.boundaries) {
    if (b.layer !== opts.layerId) continue;
    const seg = (t0: number, t1: number, color: string, w: number): void => {
      ctx.strokeStyle = color;
      ctx.lineWidth = w;
      ctx.beginPath();
      if (b.axis === 'x') {
        ctx.moveTo(px(b.coord), py(t0));
        ctx.lineTo(px(b.coord), py(t1));
      } else {
        ctx.moveTo(px(t0), py(b.coord));
        ctx.lineTo(px(t1), py(b.coord));
      }
      ctx.stroke();
    };
    const wallWidth = Math.max(1.6, fit.scale * 0.22);
    let cursor = b.emitStart;
    for (const o of [...b.openings].sort((p, q) => p.t0 - q.t0)) {
      if (o.t0 > cursor) seg(cursor, Math.min(o.t0, b.emitEnd), WALL_COLOR[b.type] ?? '#c8d2dd', wallWidth);
      seg(
        Math.max(o.t0, b.emitStart),
        Math.min(o.t1, b.emitEnd),
        OPENING_COLOR[o.kind] ?? '#7de08a',
        Math.max(1.4, wallWidth * 0.7),
      );
      cursor = Math.max(cursor, o.t1);
    }
    if (cursor < b.emitEnd) seg(cursor, b.emitEnd, WALL_COLOR[b.type] ?? '#c8d2dd', wallWidth);
  }

  // Vertical connections.
  for (const v of level.verticals) {
    if (v.spec.from_layer !== opts.layerId && v.spec.to_layer !== opts.layerId) continue;
    const [x, y, w, d] = v.footprint;
    ctx.strokeStyle = v.spec.kind === 'hatch' ? '#e0b25c' : '#ff9a6a';
    ctx.lineWidth = 1.4;
    ctx.setLineDash([4, 3]);
    ctx.strokeRect(px(x * g), py((y + d) * g), w * g * fit.scale, d * g * fit.scale);
    ctx.setLineDash([]);
  }

  // Markers.
  for (const m of level.markers) {
    if (m.layer !== opts.layerId) continue;
    const style = MARKER_STYLE[m.kind] ?? { color: 0xffffff, label: m.kind };
    ctx.fillStyle = `#${style.color.toString(16).padStart(6, '0')}`;
    ctx.beginPath();
    ctx.arc(px(m.pos[0]), py(m.pos[1]), Math.max(3, fit.scale * 0.4), 0, Math.PI * 2);
    ctx.fill();
    if (opts.showLabels && m.label) {
      ctx.fillStyle = 'rgba(235,240,246,0.92)';
      ctx.font = '11px ui-sans-serif, system-ui, sans-serif';
      ctx.fillText(m.label, px(m.pos[0]) + 8, py(m.pos[1]) + 4);
    }
  }

  // Space labels.
  if (opts.showLabels && layer) {
    const acc = new Map<string, { x: number; y: number; n: number }>();
    for (const [k, space] of occ) {
      const i = k.indexOf(',');
      const x = Number(k.slice(0, i));
      const y = Number(k.slice(i + 1));
      let a = acc.get(space);
      if (!a) acc.set(space, (a = { x: 0, y: 0, n: 0 }));
      a.x += x + 0.5;
      a.y += y + 0.5;
      a.n++;
    }
    ctx.font = '600 11px ui-sans-serif, system-ui, sans-serif';
    ctx.textAlign = 'center';
    for (const [id, a] of acc) {
      const sp = layer.spaces.find((s) => s.id === id);
      ctx.fillStyle = 'rgba(226,234,243,0.86)';
      ctx.fillText(sp?.label ?? id, px((a.x / a.n) * g), py((a.y / a.n) * g));
    }
    ctx.textAlign = 'start';
  }

  // Player.
  if (opts.player) {
    const x = px(opts.player.x);
    const y = py(opts.player.y);
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(-opts.player.yaw);
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = 'rgba(0,0,0,0.65)';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(0, -9);
    ctx.lineTo(6, 7);
    ctx.lineTo(0, 3.5);
    ctx.lineTo(-6, 7);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }
}

/** Layer whose walking surface is nearest below the given elevation. */
export function layerAt(level: CompiledLevel, z: number): string {
  const sorted = [...level.spec.layers].sort((a, b) => a.z - b.z);
  let best = sorted[0]?.id ?? '';
  for (const l of sorted) if (z >= l.z - 1.2) best = l.id;
  return best;
}
